import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Hide dropdown immediately when scrolling to prevent ghost visibility
    window.addEventListener('scroll', this.#handleScroll, {
      signal: this.#abortController.signal,
      passive: true,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item) return;
    
    // Check if this item actually has a submenu
    const hasSubmenu = item.getAttribute('aria-haspopup') === 'true';
    if (!hasSubmenu) return;
    
    if (item == this.#state.activeItem) return;

    const isDefaultSlot = item.slot === '' || item.slot == null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      this.#setToggleExpanded(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.#setToggleExpanded(item, true);

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    if (submenu) {
      if (submenu.matches(':hover')) return;
      if (submenu.contains(document.activeElement)) return;
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.#setToggleExpanded(item, false);
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Toggle accordion state for a menu item with a submenu.
   * @param {Event} event
   */
  toggleAccordion = (event) => {
    if (!(event.target instanceof Element)) return;
    event.preventDefault();

    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem) return;

    const menuItem = listItem.querySelector('[ref="menuitem"]');
    if (!(menuItem instanceof HTMLElement)) return;

    const isExpanded = menuItem.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      this.#deactivate(menuItem);
      return;
    }

    this.#activateItem(menuItem);
  };

  /**
   * Handle keyboard toggle for accordion button.
   * @param {KeyboardEvent} event
   */
  handleToggleKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.toggleAccordion(event);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle scroll event - immediately hide any open dropdown
   */
  #handleScroll = () => {
    if (!this.#state.activeItem) return;
    
    // Cancel any pending activation/deactivation
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    
    // Immediately deactivate
    const item = this.#state.activeItem;
    if (item) {
      this.style.setProperty('--submenu-height', '0px');
      this.style.setProperty('--submenu-opacity', '0');
      this.dataset.overflowExpanded = 'false';
      
      const submenu = findSubmenu(item);
      if (submenu) {
        delete submenu.dataset.active;
      }
      
      this.#state.activeItem = null;
      this.ariaExpanded = 'false';
      item.ariaExpanded = 'false';
      this.#setToggleExpanded(item, false);
      item.removeAttribute('data-animating');
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Set toggle button aria-expanded for a menu item.
   * @param {HTMLElement} item
   * @param {boolean} isExpanded
   */
  #setToggleExpanded = (item, isExpanded) => {
    const listItem = item.closest('.menu-list__list-item');
    const toggle = listItem?.querySelector('.menu-list__toggle');
    if (toggle instanceof HTMLElement) {
      toggle.setAttribute('aria-expanded', isExpanded.toString());
    }
  };

  /**
   * Activate a menu item directly.
   * @param {HTMLElement} item
   */
  #activateItem = (item) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (item == this.#state.activeItem) return;

    const isDefaultSlot = item.slot === '' || item.slot == null;
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      this.#setToggleExpanded(previouslyActiveItem, false);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.#setToggleExpanded(item, true);

    let submenu = findSubmenu(item);
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element;
  }

  const listItem = element.closest('.menu-list__list-item');
  if (!listItem) return null;

  if (listItem.getAttribute('slot') === 'more') {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(listItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  // Look for submenu in the parent list item, not just the immediate parent
  // The submenu is a sibling of .menu-list__trigger, not a child
  const listItem = element.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
